import { useState, useMemo, useEffect } from 'react';

// Shared modal for assigning a conflicted fixture to a date/ground/time.
// Used by both SchedulePage (direct save) and GeneratorPage (capture draft).
//
// Props
//   homeTeam, awayTeam : { id, name, short_name }
//   divisionName       : string (affects allowed times: Premier → 12:30/16:00)
//   fixtureId          : optional — excluded from team-busy checks (self-conflict fixture)
//   allFixtures        : array of fixtures (id, home_team_id, away_team_id, ground_id, match_date, match_time, status)
//   grounds            : array of { id, name, ground_type }
//   title              : header label (default: "Assign Conflict Date")
//   onCancel           : () => void
//   onSave             : ({ date, ground_id, time }) => void
export default function AssignConflictModal({
  homeTeam, awayTeam, divisionName, divisionId = null,
  fixtureId = null,
  allFixtures = [], grounds = [], teams = [],
  blackoutDates = [],  // array of 'YYYY-MM-DD' — dates where neither team can play (division no-game-day, team no-game-day, global blackout)
  groundAvailability = null, // optional array of { ground_id, available_date, status } — when provided, only AVAILABLE (ground, date) combos are suggested. Null → no filter (legacy/permissive).
  groundRules = [],     // array of ground_rule condition values: { ground_id, allowed_times, premier_priority, allowed_division_ids, division_restriction }
  groundDivRestrictions = [], // array of { ground_id, allowed_divisions: [divisionName,...] } from ground_division_restriction conditions (e.g. Troy → Premier)
  onRescheduleFixture, // optional: (fixture) => void — wired by parent to open nested modal for rescheduling the blocking fixture
  preselectDate = null, // optional: when the parent sets this, the modal jumps to that date (used after a reschedule frees a Sunday)
  title = '📅 Assign Conflict Date',
  onCancel, onSave,
}) {
  const blackoutSet = useMemo(() => new Set((blackoutDates || []).map(d => String(d).split('T')[0])), [blackoutDates]);

  // Set of "groundId|YYYY-MM-DD" keys where the ground is AVAILABLE (status='available').
  // If caller passed null/undefined, we treat every ground as available on every date (legacy).
  const availableGroundDateSet = useMemo(() => {
    if (!groundAvailability) return null;
    const s = new Set();
    groundAvailability.forEach(r => {
      if (r.status !== 'available') return;
      const d = String(r.available_date || '').split('T')[0];
      if (!d) return;
      s.add(`${r.ground_id}|${d}`);
    });
    return s;
  }, [groundAvailability]);

  const isGroundAvailable = (groundId, dateStr) => {
    if (!availableGroundDateSet) return true;
    return availableGroundDateSet.has(`${groundId}|${dateStr}`);
  };

  // Build lookup tables from the raw condition arrays.
  const groundRuleMap = useMemo(() => {
    const m = {};
    (groundRules || []).forEach(r => { if (r.ground_id) m[r.ground_id] = r; });
    return m;
  }, [groundRules]);

  const groundDivRestrictionMap = useMemo(() => {
    const m = {};
    (groundDivRestrictions || []).forEach(r => {
      if (!r.ground_id) return;
      m[r.ground_id] = new Set((r.allowed_divisions || []).map(d => String(d || '').toLowerCase()));
    });
    return m;
  }, [groundDivRestrictions]);

  // Per-ground allowed start times ("HH:MM:SS") from ground_rule.allowed_times.
  const groundTimesMap = useMemo(() => {
    const m = {};
    Object.values(groundRuleMap).forEach(r => {
      if (!r.allowed_times) return;
      const parsed = String(r.allowed_times).split(/[,;]/).map(raw => {
        const s = raw.trim().toUpperCase();
        const m2 = s.match(/^(\d{1,2})(?:[.:](\d{2}))?\s*(AM|PM)?$/);
        if (!m2) return null;
        let h = parseInt(m2[1], 10);
        const min = parseInt(m2[2] || '0', 10);
        const ap = m2[3];
        if (ap === 'PM' && h < 12) h += 12;
        if (ap === 'AM' && h === 12) h = 0;
        return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}:00`;
      }).filter(Boolean);
      if (parsed.length) m[r.ground_id] = parsed;
    });
    return m;
  }, [groundRuleMap]);

  // Decide if the current division can play at a given ground, mirroring the
  // scheduler's canUseGround logic (backend/src/services/scheduleGenerator.js).
  const canUseGround = (groundId) => {
    const isPremierGround = premierGroundIds.has(groundId);
    // Premier division → premier grounds only.
    if (isPremier) return isPremierGround;
    // Non-Premier at a standard ground → always allowed.
    if (!isPremierGround) return true;
    // Non-Premier at a premier ground — check explicit restrictions.
    const divRestrict = groundDivRestrictionMap[groundId]; // Set<lowercase division name>
    if (divRestrict && divRestrict.size > 0) {
      return divRestrict.has(String(divisionName || '').toLowerCase());
    }
    const rule = groundRuleMap[groundId];
    if (rule?.division_restriction === 'premier_only') return false;
    if (rule?.premier_priority && Array.isArray(rule.allowed_division_ids) && rule.allowed_division_ids.length > 0) {
      return !!divisionId && rule.allowed_division_ids.includes(divisionId);
    }
    // No explicit restriction → allowed (legacy spillover).
    return true;
  };

  const teamById = useMemo(() => Object.fromEntries(teams.map(t => [t.id, t])), [teams]);
  const teamLabel = (id) => teamById[id]?.short_name || teamById[id]?.name || '—';
  const [date, setDate] = useState('');
  const [groundId, setGroundId] = useState('');
  const [time, setTime] = useState('');

  const isPremier = (divisionName || '').toLowerCase() === 'premier';
  const defaultTimes = isPremier ? ['12:30:00', '16:00:00'] : ['08:45:00', '12:30:00', '16:00:00'];
  const premierGroundIds = useMemo(
    () => new Set(grounds.filter(g => g.ground_type === 'premier').map(g => g.id)),
    [grounds]
  );
  const groundById = useMemo(() => Object.fromEntries(grounds.map(g => [g.id, g])), [grounds]);

  const normalizeDate = (d) => String(d || '').split('T')[0];
  const normalizeTime = (t) => String(t || '').substring(0, 8);
  const fmtTime = (t) => {
    const [h, m] = String(t || '').split(':');
    const n = parseInt(h, 10);
    if (isNaN(n)) return t;
    const period = n >= 12 ? 'PM' : 'AM';
    const h12 = n % 12 === 0 ? 12 : n % 12;
    return `${h12}:${m || '00'} ${period}`;
  };

  // ── Per-team busy roster ────────────────────────────────────────────────────
  // For each team: their confirmed games (excluding self fixture), with date/ground/time.
  const teamBusy = (teamId) =>
    allFixtures
      .filter(f =>
        f.id !== fixtureId &&
        f.status === 'confirmed' &&
        (f.home_team_id === teamId || f.away_team_id === teamId)
      )
      .map(f => ({
        fixture_id: f.id,
        home_team_id: f.home_team_id,
        away_team_id: f.away_team_id,
        division_id: f.division_id,
        division_name: f.division_name,
        match_type: f.match_type,
        round_number: f.round_number,
        date: normalizeDate(f.match_date),
        time: normalizeTime(f.match_time),
        ground_id: f.ground_id,
        ground_name: groundById[f.ground_id]?.name || '—',
        opponent_id: f.home_team_id === teamId ? f.away_team_id : f.home_team_id,
      }))
      .filter(x => x.date)
      .sort((a, b) => a.date.localeCompare(b.date));

  const homeBusy = useMemo(() => homeTeam ? teamBusy(homeTeam.id) : [], [allFixtures, homeTeam, fixtureId]);
  const awayBusy = useMemo(() => awayTeam ? teamBusy(awayTeam.id) : [], [allFixtures, awayTeam, fixtureId]);

  const homeDatesSet = useMemo(() => new Set(homeBusy.map(x => x.date)), [homeBusy]);
  const awayDatesSet = useMemo(() => new Set(awayBusy.map(x => x.date)), [awayBusy]);

  // ── Slot usage across the season ─────────────────────────────────────────────
  const usedSlots = useMemo(() => {
    const m = {};
    allFixtures.forEach(f => {
      if (f.id === fixtureId) return;
      if (f.status !== 'confirmed' || !f.match_date || !f.ground_id) return;
      const k = `${normalizeDate(f.match_date)}|${f.ground_id}|${normalizeTime(f.match_time)}`;
      m[k] = (m[k] || 0) + 1;
    });
    return m;
  }, [allFixtures, fixtureId]);

  const allDates = useMemo(
    () => [...new Set(allFixtures.map(f => normalizeDate(f.match_date)).filter(Boolean))].sort(),
    [allFixtures]
  );

  // Per-ground allowed start times for this division, mirroring the scheduler:
  //   • Premier division → always uses C7 times (12:30/16:00) at any premier ground
  //   • Non-Premier at a premier ground → spillover window [08:45, 12:30, 16:00]
  //   • Non-Premier at a standard ground → ground_rule.allowed_times if set, else default
  const timesForGround = (groundId) => {
    if (isPremier) return defaultTimes;
    if (premierGroundIds.has(groundId)) return ['08:45:00', '12:30:00', '16:00:00'];
    return groundTimesMap[groundId] || defaultTimes;
  };

  // Dates where ANY premium/standard slot is free (respecting division restrictions AND ground availability)
  const hasFreeSlotOnDate = (d) => {
    for (const g of grounds) {
      if (!canUseGround(g.id)) continue;
      if (!isGroundAvailable(g.id, d)) continue; // ground isn't open on this date
      for (const t of timesForGround(g.id)) {
        const k = `${d}|${g.id}|${t}`;
        if (!usedSlots[k]) return true;
      }
    }
    return false;
  };

  const bothFreeDates = useMemo(
    () => allDates.filter(d =>
      !homeDatesSet.has(d) &&
      !awayDatesSet.has(d) &&
      !blackoutSet.has(d) &&
      hasFreeSlotOnDate(d)
    ),
    [allDates, homeDatesSet, awayDatesSet, blackoutSet, usedSlots, grounds, isPremier]
  );

  // Fallback suggestions: when no Sunday has BOTH teams free, show Sundays where
  // exactly ONE team is free. Admin can see which team is blocking and what
  // they're playing on that date, then decide to swap/skip/flag.
  const singleTeamFreeDates = useMemo(() => {
    // TEMP TEST SWITCH — forces fallback panel to appear so it can be seen on existing data.
    // Revert to `bothFreeDates.length > 0` before shipping.
    if (bothFreeDates.length > 99) return [];
    const out = [];
    const homeBusyByDate = Object.fromEntries(homeBusy.map(g => [g.date, g]));
    const awayBusyByDate = Object.fromEntries(awayBusy.map(g => [g.date, g]));
    for (const d of allDates) {
      if (!hasFreeSlotOnDate(d)) continue;
      if (blackoutSet.has(d)) continue; // no-game day / blackout for this division or teams — not a usable suggestion
      const homeBlocked = homeDatesSet.has(d);
      const awayBlocked = awayDatesSet.has(d);
      if (homeBlocked && awayBlocked) continue;
      if (!homeBlocked && !awayBlocked) continue; // would be in bothFreeDates
      const busyTeam = homeBlocked ? homeTeam : awayTeam;
      const freeTeam = homeBlocked ? awayTeam : homeTeam;
      const g = homeBlocked ? homeBusyByDate[d] : awayBusyByDate[d];
      out.push({
        date: d,
        busyTeamName: busyTeam?.short_name || busyTeam?.name || '—',
        freeTeamName: freeTeam?.short_name || freeTeam?.name || '—',
        busyFixture: g, // { ground_name, time, opponent_id }
      });
    }
    return out;
  }, [bothFreeDates, allDates, homeDatesSet, awayDatesSet, blackoutSet, homeBusy, awayBusy, homeTeam, awayTeam]);

  // ── When a date is picked, derive the free ground×time combos ────────────────
  const freeSlotsOnSelectedDate = useMemo(() => {
    if (!date) return [];
    const out = [];
    for (const g of grounds) {
      if (!canUseGround(g.id)) continue;
      if (!isGroundAvailable(g.id, date)) continue; // ground not open that date
      for (const t of timesForGround(g.id)) {
        const k = `${date}|${g.id}|${t}`;
        if (!usedSlots[k]) out.push({ ground_id: g.id, ground_name: g.name, ground_type: g.ground_type, time: t });
      }
    }
    return out;
  }, [date, grounds, usedSlots, isPremier, premierGroundIds, groundTimesMap, availableGroundDateSet]);

  const freeGroundsForDate = useMemo(() => {
    const seen = new Set();
    const out = [];
    freeSlotsOnSelectedDate.forEach(s => {
      if (!seen.has(s.ground_id)) { seen.add(s.ground_id); out.push({ id: s.ground_id, name: s.ground_name, ground_type: s.ground_type }); }
    });
    return out;
  }, [freeSlotsOnSelectedDate]);

  const freeTimesForGroundOnDate = useMemo(() => {
    if (!groundId) return [];
    return freeSlotsOnSelectedDate.filter(s => s.ground_id === groundId).map(s => s.time);
  }, [freeSlotsOnSelectedDate, groundId]);

  // Clear dependent fields when date or ground changes
  useEffect(() => { setGroundId(''); setTime(''); }, [date]);
  useEffect(() => { setTime(''); }, [groundId]);

  // When the parent hands us a date (e.g. a Sunday that was just freed by
  // rescheduling a blocking fixture), jump to it so the admin doesn't have to
  // rediscover which date opened up.
  useEffect(() => {
    const d = preselectDate ? String(preselectDate).split('T')[0] : '';
    if (d) setDate(d);
  }, [preselectDate]);

  const homeName = homeTeam?.short_name || homeTeam?.name || '—';
  const awayName = awayTeam?.short_name || awayTeam?.name || '—';
  // Hard guard: prevent a save that would double-book either team. A date with
  // either team still in the busy set is NEVER a valid assignment.
  const homeBusyOnDate = !!date && homeDatesSet.has(date);
  const awayBusyOnDate = !!date && awayDatesSet.has(date);
  const blackedOut     = !!date && blackoutSet.has(date);
  const canSave = !!date && !!groundId && !!time
    && !homeBusyOnDate && !awayBusyOnDate && !blackedOut;

  // Small helpers for rendering
  const formatShortDate = (d) => {
    const [, m, dd] = (d || '').split('-');
    return m && dd ? `${parseInt(m)}/${parseInt(dd)}` : d;
  };

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
      onClick={onCancel}
    >
      <div
        style={{
          background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10,
          padding: 20, minWidth: 640, maxWidth: 780, maxHeight: '85vh', overflow: 'auto',
        }}
        onClick={e => e.stopPropagation()}
      >
        <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 800, color: 'var(--text)' }}>{title}</h3>
        <div style={{ fontSize: 12, color: 'var(--mid)', marginBottom: 14 }}>
          <strong>{homeName}</strong> vs <strong>{awayName}</strong>
          {divisionName && <span style={{ color: 'var(--dim)' }}> — {divisionName}</span>}
        </div>

        {/* Two-column team roster */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
          {[{ team: homeTeam, name: homeName, busy: homeBusy, color: 'var(--blue)' },
            { team: awayTeam, name: awayName, busy: awayBusy, color: 'var(--accent)' }].map((col, i) => (
            <div key={i} style={{
              padding: '10px 12px', background: 'var(--card)', borderRadius: 8,
              border: '1px solid var(--border)',
            }}>
              <div style={{
                fontSize: 10, fontWeight: 800, color: col.color, textTransform: 'uppercase',
                letterSpacing: .4, marginBottom: 6,
              }}>
                {col.name} · {col.busy.length} games scheduled
              </div>
              {col.busy.length === 0 ? (
                <div style={{ fontSize: 11, color: 'var(--dim)' }}>No games scheduled yet</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3, maxHeight: 180, overflow: 'auto' }}>
                  {col.busy.map((g, j) => (
                    <div key={j} style={{
                      display: 'grid', gridTemplateColumns: '52px 1fr 62px', gap: 6, alignItems: 'center',
                      fontSize: 10, fontFamily: "'Roboto Mono', monospace",
                    }}>
                      <span style={{ color: 'var(--mid)', fontWeight: 700 }}>{formatShortDate(g.date)}</span>
                      <span style={{ color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.ground_name}</span>
                      <span style={{ color: 'var(--dim)' }}>{fmtTime(g.time)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Both-teams-free Sundays */}
        <div style={{
          padding: '10px 12px', background: 'var(--card)', borderRadius: 8,
          border: '1px solid var(--border)', marginBottom: 14,
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--dim)', textTransform: 'uppercase', marginBottom: 6 }}>
            📊 Both teams free on ({bothFreeDates.length} date{bothFreeDates.length !== 1 ? 's' : ''})
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {bothFreeDates.length === 0 ? (
              <span style={{ fontSize: 11, color: 'var(--red)' }}>No Sunday available where both teams are free</span>
            ) : (
              bothFreeDates.map(d => (
                <button
                  key={d} type="button"
                  style={{
                    fontSize: 10, padding: '3px 8px', borderRadius: 4, cursor: 'pointer',
                    background: date === d ? 'var(--accent)' : 'rgba(0,200,150,.08)',
                    color: date === d ? 'white' : 'var(--accent)',
                    border: '1px solid rgba(0,200,150,.3)',
                    fontFamily: "'Roboto Mono',monospace",
                  }}
                  onClick={() => setDate(d)}
                >
                  {d.substring(5)}
                </button>
              ))
            )}
          </div>
        </div>

        {/* Fallback: single-team free dates when no both-free date exists */}
        {/* TEMP TEST: relaxed guard so panel shows even when both-free dates exist. Revert to `bothFreeDates.length === 0` */}
        {singleTeamFreeDates.length > 0 && (
          <div style={{
            padding: '10px 12px', background: 'rgba(245,158,11,.06)', borderRadius: 8,
            border: '1px solid rgba(245,158,11,.3)', marginBottom: 14,
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gold)', textTransform: 'uppercase', marginBottom: 6 }}>
              💡 Single-team free Sundays ({singleTeamFreeDates.length})
            </div>
            <div style={{ fontSize: 10, color: 'var(--mid)', marginBottom: 8, lineHeight: 1.5 }}>
              No Sunday has both teams free. These dates have one team available — to use one, either
              reschedule the other team's conflicting game, or skip/flag this fixture.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 180, overflow: 'auto' }}>
              {singleTeamFreeDates.map(s => {
                const opponentLabel = teamLabel(s.busyFixture?.opponent_id);
                const canReschedule = !!(onRescheduleFixture && s.busyFixture?.fixture_id);
                return (
                  <div
                    key={s.date}
                    onClick={() => setDate(s.date)}
                    style={{
                      display: 'grid', gridTemplateColumns: '60px 1fr auto auto', gap: 8, alignItems: 'center',
                      fontSize: 10, textAlign: 'left',
                      padding: '5px 8px', borderRadius: 5, cursor: 'pointer',
                      background: date === s.date ? 'rgba(245,158,11,.18)' : 'var(--surface)',
                      border: '1px solid rgba(245,158,11,.2)',
                      fontFamily: "'Roboto Mono',monospace", color: 'var(--text)',
                    }}
                  >
                    <span style={{ color: 'var(--gold)', fontWeight: 700 }}>{s.date.substring(5)}</span>
                    <span style={{ color: 'var(--mid)' }}>
                      <strong style={{ color: 'var(--red)' }}>{s.busyTeamName}</strong> busy
                      {s.busyFixture && (
                        <> · {s.busyFixture.ground_name} · {fmtTime(s.busyFixture.time)}
                        {opponentLabel !== '—' && <> vs {opponentLabel}</>}</>
                      )}
                    </span>
                    <span style={{ color: 'var(--accent)', fontSize: 9 }}>
                      {s.freeTeamName} free
                    </span>
                    {canReschedule ? (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onRescheduleFixture({
                          id: s.busyFixture.fixture_id,
                          home_team_id: s.busyFixture.home_team_id,
                          away_team_id: s.busyFixture.away_team_id,
                          division_id: s.busyFixture.division_id,
                          division_name: s.busyFixture.division_name,
                          match_type: s.busyFixture.match_type,
                          round_number: s.busyFixture.round_number,
                          match_date: s.busyFixture.date,
                          match_time: s.busyFixture.time,
                          ground_id: s.busyFixture.ground_id,
                          busyTeamName: s.busyTeamName,
                          opponentName: opponentLabel,
                        }); }}
                        style={{
                          fontSize: 9, padding: '3px 6px', borderRadius: 4, cursor: 'pointer',
                          background: 'rgba(74,144,217,.15)', color: 'var(--blue)',
                          border: '1px solid rgba(74,144,217,.4)', fontWeight: 700,
                          whiteSpace: 'nowrap',
                        }}
                        title={`Reschedule ${s.busyTeamName} vs ${opponentLabel}`}
                      >
                        ↻ Reschedule
                      </button>
                    ) : <span />}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Assignment form — dropdowns filtered to actually-free combos once a date is picked */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--dim)', textTransform: 'uppercase', marginBottom: 3 }}>Date</div>
            <input
              type="date" className="input" style={{ fontSize: 12, width: '100%' }}
              value={date} onChange={e => setDate(e.target.value)}
            />
          </div>
          <div>
            <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--dim)', textTransform: 'uppercase', marginBottom: 3 }}>Ground</div>
            <select
              className="input select" style={{ fontSize: 12, width: '100%' }}
              value={groundId} onChange={e => setGroundId(e.target.value)} disabled={!date}
            >
              <option value="">{date ? (freeGroundsForDate.length ? 'Select ground…' : 'No free grounds') : 'Pick date first'}</option>
              {freeGroundsForDate.map(g => (
                <option key={g.id} value={g.id}>
                  {g.name} {g.ground_type === 'premier' ? '⭐' : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--dim)', textTransform: 'uppercase', marginBottom: 3 }}>Time</div>
            <select
              className="input select" style={{ fontSize: 12, width: '100%' }}
              value={time} onChange={e => setTime(e.target.value)} disabled={!groundId}
            >
              <option value="">{groundId ? 'Select time…' : 'Pick ground first'}</option>
              {freeTimesForGroundOnDate.map(t => (
                <option key={t} value={t.substring(0, 5)}>{fmtTime(t)}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Hint: how many slots free for the chosen date */}
        {date && !homeBusyOnDate && !awayBusyOnDate && !blackedOut && (
          <div style={{ fontSize: 10, color: 'var(--dim)', marginBottom: 12 }}>
            {freeSlotsOnSelectedDate.length} free slot{freeSlotsOnSelectedDate.length !== 1 ? 's' : ''} on {date}
            {isPremier ? ' (Premier grounds only)' : ''}
          </div>
        )}

        {/* Hard-block warning: picked date double-books a team or hits a blackout */}
        {date && (homeBusyOnDate || awayBusyOnDate || blackedOut) && (
          <div style={{
            padding: '8px 10px', borderRadius: 6, marginBottom: 12,
            background: 'rgba(239,68,68,.08)', border: '1px solid rgba(239,68,68,.3)',
            fontSize: 11, color: 'var(--red)', lineHeight: 1.5,
          }}>
            ⚠ Cannot assign {date}:
            {blackedOut && ' this is a no-game day for one of the teams / division.'}
            {homeBusyOnDate && !blackedOut && (
              <> <strong>{homeTeam?.short_name || 'Home'}</strong> already plays on this date
                 — would create a double-booking.</>
            )}
            {awayBusyOnDate && !homeBusyOnDate && !blackedOut && (
              <> <strong>{awayTeam?.short_name || 'Away'}</strong> already plays on this date
                 — would create a double-booking.</>
            )}
            <div style={{ marginTop: 4, fontWeight: 700 }}>Pick a different date (or use ↻ Reschedule on the single-team-free list above).</div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          <button
            type="button" className="btn btn-primary"
            disabled={!canSave}
            style={{ opacity: canSave ? 1 : 0.55, cursor: canSave ? 'pointer' : 'not-allowed' }}
            onClick={() => onSave({ date, ground_id: groundId, time })}
          >
            Save Assignment
          </button>
        </div>
      </div>
    </div>
  );
}
